import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { publicAPI, authAPI } from '../services/api';
import './Login.css';

const Login = () => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'mobile'>('mobile');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(() => sessionStorage.getItem('loginError') || '');
  const [successMessage, setSuccessMessage] = useState(() => sessionStorage.getItem('loginSuccess') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState({
    enabled: false,
    title: '',
    description: ''
  });
  const [securityConfig, setSecurityConfig] = useState<{
    requireOtpAfterEmailLogin: boolean;
    require2FA: boolean;
    otpLoginEnabled: boolean;
    mobileLoginEnabled: boolean;
    emailLoginEnabled: boolean;
  } | null>(null);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);
  
  // Real-time data states
  const [transactions, setTransactions] = useState<Array<{time: string, amount: string}>>([]);
  const [totalVolume, setTotalVolume] = useState(12456789);
  const [transactionCount, setTransactionCount] = useState(1234);
  const [activeUsers, setActiveUsers] = useState(567);
  const [binaryCode, setBinaryCode] = useState<string[]>([]);
  const [candlestickData, setCandlestickData] = useState<Array<Array<{open: number, close: number, high: number, low: number}>>>([]);
  
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  const clearLoginAlerts = () => {
    sessionStorage.removeItem('loginError');
    sessionStorage.removeItem('loginSuccess');
    setError('');
    setSuccessMessage('');
  };

  // Ensure background video plays (required on some browsers)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const play = () => video.play().catch(() => {});
    play();
    video.addEventListener('loadeddata', play);
    return () => video.removeEventListener('loadeddata', play);
  }, []);

  // Persist alerts to sessionStorage so they survive page reload
  useEffect(() => {
    if (error) sessionStorage.setItem('loginError', error);
    else sessionStorage.removeItem('loginError');
  }, [error]);
  useEffect(() => {
    if (successMessage) sessionStorage.setItem('loginSuccess', successMessage);
    else sessionStorage.removeItem('loginSuccess');
  }, [successMessage]);

  // Check maintenance mode on component mount
  useEffect(() => {
    checkMaintenanceMode();
  }, []);

  // Initialize transactions
  useEffect(() => {
    const initialTransactions: Array<{time: string, amount: string}> = [];
    const now = new Date();
    
    for (let i = 0; i < 8; i++) {
      const time = new Date(now.getTime() - i * 30000); // 30 seconds apart
      const hours = String(time.getHours()).padStart(2, '0');
      const minutes = String(time.getMinutes()).padStart(2, '0');
      const seconds = String(time.getSeconds()).padStart(2, '0');
      const amount = (Math.random() * 5000 + 500).toFixed(2);
      
      initialTransactions.push({
        time: `${hours}:${minutes}:${seconds}`,
        amount: `+$${parseFloat(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
      });
    }
    
    setTransactions(initialTransactions);
  }, []);

  // Update transactions in real-time
  useEffect(() => {
    const interval = setInterval(() => {
      setTransactions(prev => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const newAmount = (Math.random() * 5000 + 500).toFixed(2);
        
        const newTransaction = {
          time: `${hours}:${minutes}:${seconds}`,
          amount: `+$${parseFloat(newAmount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
        };
        
        // Add new transaction at the top, keep last 8
        return [newTransaction, ...prev.slice(0, 7)];
      });
    }, 4000); // Update every 4 seconds (optimized)

    return () => clearInterval(interval);
  }, []);

  // Generate random binary code
  const generateBinaryCode = () => {
    const codes: string[] = [];
    for (let i = 0; i < 3; i++) {
      const binary = Array.from({ length: 8 }, () => 
        Math.floor(Math.random() * 2)
      ).join('');
      codes.push(binary);
    }
    return codes.join(' ');
  };

  // Initialize and update binary code in real-time
  useEffect(() => {
    // Initialize
    setBinaryCode([generateBinaryCode(), generateBinaryCode(), generateBinaryCode()]);
    
    // Update binary code every 3 seconds (reduced frequency)
    const interval = setInterval(() => {
      setBinaryCode([generateBinaryCode(), generateBinaryCode(), generateBinaryCode()]);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Generate candlestick data
  const generateCandlestickData = () => {
    const charts: Array<Array<{open: number, close: number, high: number, low: number}>> = [];
    
    for (let chart = 0; chart < 3; chart++) {
      const candles: Array<{open: number, close: number, high: number, low: number}> = [];
      let basePrice = 50 + Math.random() * 40;
      
      for (let i = 0; i < 12; i++) {
        const open = basePrice;
        const change = (Math.random() - 0.5) * 15;
        const close = Math.max(20, Math.min(100, open + change));
        const high = Math.max(open, close) + Math.random() * 20;
        const low = Math.min(open, close) - Math.random() * 20;
        
        candles.push({ open, close, high, low });
        basePrice = close;
      }
      
      charts.push(candles);
    }
    
    return charts;
  };

  // Initialize and update candlestick data
  useEffect(() => {
    // Initialize immediately
    const initialData = generateCandlestickData();
    setCandlestickData(initialData);
    
    // Update candlestick data every 6 seconds (reduced frequency)
    const interval = setInterval(() => {
      setCandlestickData(generateCandlestickData());
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  // Update stats in real-time (optimized frequency)
  useEffect(() => {
    const interval = setInterval(() => {
      // Update total volume (increase by random amount)
      setTotalVolume(prev => {
        const increase = Math.floor(Math.random() * 8000 + 2000);
        return prev + increase;
      });
      
      // Update transaction count (increase by 2-5)
      setTransactionCount(prev => prev + Math.floor(Math.random() * 4) + 2);
      
      // Update active users (fluctuate more)
      setActiveUsers(prev => {
        const change = Math.floor(Math.random() * 8) - 3; // -3 to +4
        return Math.max(500, Math.min(650, prev + change));
      });
    }, 5000); // Update every 5 seconds (reduced frequency)

    return () => clearInterval(interval);
  }, []);

  const checkMaintenanceMode = async () => {
    try {
      const response = await publicAPI.getMaintenanceStatus();
      if (response.data.success) {
        const data = response.data.data;
        setMaintenanceMode({
          enabled: data.enabled,
          title: data.title,
          description: data.description,
        });

        const sec = data.security || {};
        const otpLoginEnabled = sec.otp_login_enabled !== false;
        const mobileLoginEnabled = sec.mobile_login_enabled !== false;
        const emailLoginEnabled = sec.email_login_enabled !== false;
        setSecurityConfig({
          requireOtpAfterEmailLogin: sec.require_otp_after_email_login !== false,
          require2FA: !!sec.require_2fa,
          otpLoginEnabled,
          mobileLoginEnabled,
          emailLoginEnabled,
        });
        // Prioritize mobile/email based on which methods are enabled (not otp_login_enabled)
        if (mobileLoginEnabled && !emailLoginEnabled) {
          setLoginMethod('mobile');
        } else if (!mobileLoginEnabled && emailLoginEnabled) {
          setLoginMethod('email');
        } else if (!otpLoginEnabled) {
          setLoginMethod('email');
        }
      }
    } catch (error) {
      console.error('Failed to check maintenance mode:', error);
      setSecurityConfig({
        requireOtpAfterEmailLogin: true,
        require2FA: false,
        otpLoginEnabled: true,
        mobileLoginEnabled: true,
        emailLoginEnabled: true,
      });
    } finally {
      setCheckingMaintenance(false);
    }
  };

  // Function to mask phone number (show only last 4 digits)
  const maskPhoneNumber = (phone: string) => {
    if (!phone || phone.length < 4) return phone;
    const last4 = phone.slice(-4);
    const masked = '*'.repeat(phone.length - 4);
    return `${masked}${last4}`;
  };

  const handleSendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      setSuccessMessage('');
      return;
    }
    if (!password || password.trim() === '') {
      setError('Please enter your password');
      setSuccessMessage('');
      return;
    }

    setIsSendingOtp(true);
    setError('');
    setSuccessMessage('');

    try {
      await authAPI.sendOtp(mobile, password);
      setOtpSent(true);
      setError('');
      const maskedPhone = maskPhoneNumber(mobile);
      setSuccessMessage(`OTP Successfully Sent to ${maskedPhone}`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
      setSuccessMessage('');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authAPI.verifyOtp(mobile, otp);
      
      if (!response.data.success) {
        setError(response.data.message || 'OTP verification failed');
        setIsLoading(false);
        return;
      }

      const { token: newToken, user: newUser } = response.data.data;
      
      // Store token and user in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      // Update AuthContext by reloading the page or using window.location
      // This ensures AuthContext picks up the new token
      const userRole = newUser?.role?.toLowerCase();
      
      // Check if maintenance mode is active and user is not admin
      if (maintenanceMode.enabled && userRole !== 'admin') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setError(`🔧 ${maintenanceMode.title}\n\n${maintenanceMode.description}\n\nOnly administrators can access the system during maintenance.`);
        setIsLoading(false);
        return;
      }
      
      // Clear persisted alerts before redirect
      clearLoginAlerts();
      if (userRole === 'admin') {
        window.location.href = '/admin';
      } else if (userRole === 'manager') {
        window.location.href = '/manager';
      } else if (userRole === 'merchant') {
        window.location.href = '/merchant';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
      setIsLoading(false);
    }
  };

  const handleEmailPasswordLogin = async () => {
    setError('');
    setIsLoading(true);

    // Check maintenance mode before allowing login
    if (maintenanceMode.enabled) {
      console.log('🔧 Maintenance mode is active - checking user role after login');
    }

    try {
      const response = await login(email, password);
      const userData = response.data.data.user;
      const userRole = userData?.role?.toLowerCase();
      
      // Check if maintenance mode is active and user is not admin
      if (maintenanceMode.enabled && userRole !== 'admin') {
        await logout();
        setError(`🔧 ${maintenanceMode.title}\n\n${maintenanceMode.description}\n\nOnly administrators can access the system during maintenance.`);
        setIsLoading(false);
        return;
      }
      
      // Store token temporarily (will be updated after OTP verification)
      const tempToken = response.data.data.token;
      localStorage.setItem('token', tempToken);
      localStorage.setItem('user', JSON.stringify(userData));

      // If security config requires 2FA/OTP after email+password, go through OTP flow
      if (securityConfig?.requireOtpAfterEmailLogin || securityConfig?.require2FA) {
        // After successful email/password login, show OTP verification step
        setShowOtpStep(true);
        setIsLoading(false);
        
        // Automatically send OTP after successful login
        try {
          const userPhone = userData?.details?.phone || userData?.phone;
          if (userPhone) {
            await authAPI.sendOtp(userPhone);
            setOtpSent(true);
            setError('');
            const maskedPhone = maskPhoneNumber(userPhone);
            setSuccessMessage(`OTP Successfully Sent to ${maskedPhone}`);
            
            // Clear success message after 5 seconds
            setTimeout(() => {
              setSuccessMessage('');
            }, 5000);
          } else {
            setError('Mobile number not found. Please contact administrator.');
            setShowOtpStep(false);
            // Still allow login without OTP if mobile not found
            clearLoginAlerts();
            if (userRole === 'admin') {
              navigate('/admin');
            } else if (userRole === 'manager') {
              navigate('/manager');
            } else if (userRole === 'merchant') {
              navigate('/merchant');
            } else {
              navigate('/dashboard');
            }
          }
        } catch (otpErr: any) {
          console.error('Failed to send OTP:', otpErr);
          setError('Login successful but failed to send OTP. Please contact administrator.');
          setShowOtpStep(false);
          // Still allow login without OTP if OTP sending fails
          clearLoginAlerts();
          if (userRole === 'admin') {
            navigate('/admin');
          } else if (userRole === 'manager') {
            navigate('/manager');
          } else if (userRole === 'merchant') {
            navigate('/merchant');
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        // OTP-after-login is disabled: go directly to dashboard based on role
        setShowOtpStep(false);
        setIsLoading(false);
        clearLoginAlerts();
        if (userRole === 'admin') {
          navigate('/admin');
        } else if (userRole === 'manager') {
          navigate('/manager');
        } else if (userRole === 'merchant') {
          navigate('/merchant');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  const handleOtpVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authAPI.verifyOtpAfterLogin(otp);
      
      if (!response.data.success) {
        setError(response.data.message || 'OTP verification failed');
        setIsLoading(false);
        return;
      }

      const { token: newToken, user: newUser } = response.data.data;
      
      // Store token and user in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      const userRole = newUser?.role?.toLowerCase();
      
      // Check if maintenance mode is active and user is not admin
      if (maintenanceMode.enabled && userRole !== 'admin') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setError(`🔧 ${maintenanceMode.title}\n\n${maintenanceMode.description}\n\nOnly administrators can access the system during maintenance.`);
        setIsLoading(false);
        return;
      }
      
      // Clear persisted alerts before redirect
      clearLoginAlerts();
      if (userRole === 'admin') {
        window.location.href = '/admin';
      } else if (userRole === 'manager') {
        window.location.href = '/manager';
      } else if (userRole === 'merchant') {
        window.location.href = '/merchant';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loginMethod === 'mobile') {
      if (!otpSent) {
        await handleSendOtp();
      } else {
        await handleOtpLogin(e);
      }
    } else {
      await handleEmailPasswordLogin();
    }
  };

  const inputBase = 'w-full py-3 px-4 pl-12 border-2 border-violet-500/60 rounded-xl bg-violet-600/70 text-white text-base placeholder-violet-200/80 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/40 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';
  const labelBase = 'block text-sm font-bold text-white uppercase tracking-wider mb-2';

  return (
    <div className="min-h-screen min-w-full flex items-center justify-center p-5 relative overflow-hidden">
      <div
        className="fixed z-0"
        style={{ left: 0, top: 0, width: '100vw', height: '100vh', minWidth: '100%', minHeight: '100%', overflow: 'hidden', backgroundColor: '#0f172a' }}
      >
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="absolute"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: '100%',
            minHeight: '100%',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        >
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
      </div>
      <div
        className="fixed pointer-events-none z-[1] bg-slate-900/60"
        style={{ left: 0, top: 0, width: '100vw', height: '100vh' }}
        aria-hidden
      />
      <div
        className="fixed pointer-events-none z-[1] bg-[linear-gradient(rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:50px_50px]"
        style={{ left: 0, top: 0, width: '100vw', height: '100vh' }}
        aria-hidden
      />
      <div className="w-full max-w-md relative z-10 min-h-0">
        <div className="rounded-2xl border-2 border-violet-500/50 bg-violet-900/30 backdrop-blur-xl shadow-2xl shadow-violet-900/30 p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="YuvanAPI Logo" className="w-44 h-auto sm:w-52 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-white uppercase tracking-wider m-0 mb-2">YUVANAPI SYSTEM</h1>
            <p className="text-sm text-violet-300/90 m-0">Sign in to continue to your account</p>
            <div className="w-28 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mt-4 rounded" />
          </div>

          {maintenanceMode.enabled && (
            <div className="flex gap-4 p-4 rounded-xl border-2 border-red-500/50 bg-red-500/20 mb-6">
              <span className="text-2xl flex-shrink-0">🔧</span>
              <div>
                <h3 className="font-bold text-red-200 m-0 mb-1">{maintenanceMode.title}</h3>
                <p className="text-sm text-red-200/90 m-0">{maintenanceMode.description}</p>
                <p className="text-sm font-bold text-red-200 mt-2">⚠️ Only administrators can access during maintenance.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-red-500/50 bg-red-500/20 text-red-200 text-sm mb-5 whitespace-pre-line">
              <span className="flex-1">{error}</span>
              <button type="button" onClick={() => setError('')} className="flex-shrink-0 p-1 rounded hover:bg-red-500/30 text-red-200" aria-label="Dismiss">✕</button>
            </div>
          )}
          {successMessage && (
            <div className="flex items-center gap-2 p-3 rounded-xl border-2 border-emerald-500/50 bg-emerald-500/20 text-emerald-200 text-sm mb-5">
              <span className="w-6 h-6 rounded-full bg-emerald-500/40 flex items-center justify-center text-sm font-bold flex-shrink-0">✓</span>
              <span className="flex-1">{successMessage}</span>
              <button type="button" onClick={() => setSuccessMessage('')} className="flex-shrink-0 p-1 rounded hover:bg-emerald-500/30 text-emerald-200" aria-label="Dismiss">✕</button>
            </div>
          )}

          {!securityConfig && (
            <div className="py-8 flex items-center justify-center">
              <span className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
              <span className="ml-3 text-violet-300">Loading login options...</span>
            </div>
          )}
          {securityConfig && !showOtpStep && securityConfig.mobileLoginEnabled && securityConfig.emailLoginEnabled && (
            <div className="flex gap-2 p-1.5 rounded-xl bg-violet-500/10 border border-violet-500/30 mb-6">
              <button
                type="button"
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold uppercase transition-all ${loginMethod === 'mobile' ? 'bg-violet-500/40 text-white border-2 border-violet-400 shadow-lg shadow-violet-500/20' : 'text-violet-300/80 border-2 border-transparent hover:text-white'}`}
                onClick={() => { setLoginMethod('mobile'); setError(''); setOtpSent(false); setOtp(''); }}
              >
                Mobile OTP
              </button>
              <button
                type="button"
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold uppercase transition-all ${loginMethod === 'email' ? 'bg-violet-500/40 text-white border-2 border-violet-400 shadow-lg shadow-violet-500/20' : 'text-violet-300/80 border-2 border-transparent hover:text-white'}`}
                onClick={() => { setLoginMethod('email'); setError(''); setOtpSent(false); setOtp(''); }}
              >
                Email + Password
              </button>
            </div>
          )}
          {securityConfig && !showOtpStep && securityConfig.emailLoginEnabled && !securityConfig.mobileLoginEnabled && (
            <p className="text-sm text-violet-300/90 mb-6">Sign in with your email and password.</p>
          )}
          {securityConfig && !showOtpStep && securityConfig.mobileLoginEnabled && !securityConfig.emailLoginEnabled && null}
          {securityConfig && !showOtpStep && !securityConfig.mobileLoginEnabled && !securityConfig.emailLoginEnabled && (
            <p className="text-sm text-red-300/90 mb-6">Login methods are currently disabled. Please contact your administrator.</p>
          )}

          {securityConfig && (securityConfig.mobileLoginEnabled || securityConfig.emailLoginEnabled) && (showOtpStep || (loginMethod === 'mobile' && otpSent) ? (
            <form onSubmit={showOtpStep ? handleOtpVerification : handleOtpLogin} className="mt-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-bold text-white uppercase tracking-wider m-0 mb-1">OTP Verification</h3>
                <p className="text-sm text-violet-300/90 m-0">Enter the OTP sent to your mobile number</p>
              </div>
              <div className="mb-5 relative">
                <label htmlFor="otp" className={labelBase}>Enter OTP</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7C7 5.67 8.46 2 12 2s5 3.67 5 5v4"/></svg>
                  </span>
                  <input id="otp" type="text" autoComplete="off" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit OTP" maxLength={6} required disabled={isLoading} className={inputBase} />
                </div>
              </div>
              <button type="submit" disabled={isLoading} className="w-full py-3.5 px-5 rounded-xl bg-violet-500/50 border-2 border-violet-400 text-white font-bold text-sm uppercase tracking-wider cursor-pointer hover:bg-violet-500/70 hover:border-violet-300 disabled:opacity-50 flex items-center justify-center gap-2">
                {isLoading && <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {isLoading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button type="button" onClick={handleSendOtp} disabled={isSendingOtp} className="w-full mt-4 py-2.5 rounded-xl border-2 border-violet-500/50 text-violet-200 text-sm font-semibold cursor-pointer hover:bg-violet-500/20 disabled:opacity-50">
                {isSendingOtp ? 'Sending...' : 'Resend OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6">
              {loginMethod === 'email' ? (
                <>
                  <div className="mb-5 relative">
                    <label htmlFor="email" className={labelBase}>Email Address</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5z"/>
                          <path d="M20 22v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <input id="email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required disabled={isLoading} className={inputBase} />
                    </div>
                  </div>
                  <div className="mb-5 relative">
                    <label htmlFor="password" className={labelBase}>Password</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </span>
                      <input id="password" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required disabled={isLoading} className={inputBase} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-5 relative">
                    <label htmlFor="mobile" className={labelBase}>Mobile Number</label>
                    <div className="relative rounded-xl border-2 border-violet-500/60 bg-violet-600/70">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M22 16.92v3.02a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3.02a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <input id="mobile" type="tel" autoComplete="off" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Enter 10-digit mobile number" maxLength={10} required disabled={isLoading || otpSent} className="login-input-mobile w-full py-3 px-4 pl-12 rounded-xl bg-transparent border-0 text-white text-base placeholder-violet-200/80 focus:ring-2 focus:ring-violet-400/40 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed" />
                    </div>
                  </div>
                  <div className="mb-5 relative">
                    <label htmlFor="mobile-password" className={labelBase}>Password</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </span>
                      <input id="mobile-password" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required disabled={isLoading || otpSent} className={inputBase} />
                    </div>
                  </div>
                  {otpSent && (
                    <div className="mb-5 relative">
                      <label htmlFor="otp-mobile" className={labelBase}>Enter OTP</label>
                      <div className="relative">
<span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </span>
                        <input id="otp-mobile" type="text" autoComplete="off" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit OTP" maxLength={6} required disabled={isLoading} className={inputBase} />
                      </div>
                    </div>
                  )}
                </>
              )}
              <button type="submit" disabled={isLoading || isSendingOtp} className="w-full py-3.5 px-5 rounded-xl bg-violet-500/50 border-2 border-violet-400 text-white font-bold text-sm uppercase tracking-wider cursor-pointer hover:bg-violet-500/70 hover:border-violet-300 disabled:opacity-50 flex items-center justify-center gap-2 mt-4">
                {(isLoading || isSendingOtp) && <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {isLoading ? (loginMethod === 'mobile' && otpSent ? 'Verifying...' : 'Signing in...') : isSendingOtp ? 'Sending OTP...' : loginMethod === 'mobile' && !otpSent ? 'LOGIN via OTP' : loginMethod === 'mobile' && otpSent ? 'Verify OTP' : 'Sign In'}
              </button>
              {loginMethod === 'mobile' && otpSent && (
                <button type="button" onClick={handleSendOtp} disabled={isSendingOtp} className="w-full mt-4 py-2.5 rounded-xl border-2 border-violet-500/50 text-violet-200 text-sm font-semibold cursor-pointer hover:bg-violet-500/20 disabled:opacity-50">
                  {isSendingOtp ? 'Sending...' : 'Resend OTP'}
                </button>
              )}
              <div className="mt-5 flex items-center justify-center gap-4 text-sm">
                <Link to="/forgot-password" className="text-violet-300 hover:text-violet-200 underline underline-offset-2">
                  Forgot Password
                </Link>
                <span className="text-violet-500/60">|</span>
                <Link to="/reset-tpin" className="text-violet-300 hover:text-violet-200 underline underline-offset-2">
                  Reset TPIN
                </Link>
              </div>
            </form>
          ))}

          <div className="mt-8 pt-6 border-t border-violet-500/30 text-center">
            <p className="text-sm text-violet-300/80 m-0">Built by <span className="font-bold text-white">Algobasket</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

